<template>
  <div>
    <v-autocomplete
        v-model="inputVal"
        outlined
        :label="label"
        :menu-props="{ bottom: true, offsetY: true }"
        :items="data"
        item-text="name"
        item-value="id"
        clearable
        hide-selected
        :hide-no-data="hideNoText"
        :loading="isLoading"
        :disabled="disabled"
        :search-input.sync="search"
        :tabindex="tabindex"
        :rules="required ? [ruleRequired($t('utils:rules.required'))] : []"
        @change="skipNextSearch"
    >
      <template v-slot:append>
        <v-icon>search</v-icon>
      </template>

      <template v-slot:no-data>
        <div class="px-3 py-2">
          <div v-if="searchStringLength > 2" class="row no-gutters align-center">
            <div class="col-10 pa-0">
              <p class="mb-0">{{ computedDataText }}</p>
            </div>
            <div class="col-2 pa-0">
              <v-btn fab x-small color="primary" elevation="0" @click="onAddClick">
                <v-icon>add</v-icon>
              </v-btn>
            </div>
          </div>

          <div v-else class="row no-gutters align-center">
            <div class="col-12 pa-0">
              <p class="mb-0">{{ computedDataText }}</p>
            </div>
          </div>
        </div>
      </template>
    </v-autocomplete>

    <MinimizedCustomerDialogForm ref="customerDialogForm" @onSave="parseCustomer" @close="search = null"/>
  </div>
</template>

<script>
import CustomerService from '@/modules/core/services/customer-service';
import DataUtils from "@/modules/core/services/data-utils";
import Customer from '@/modules/core/data/customer';
import {ruleRequired} from "@/modules/core/services/rules";
import MinimizedCustomerDialogForm from '@/modules/core/components/MinimizedCustomerDialogForm';

export default {
  name: "CustomerTypeahead",
  components: {MinimizedCustomerDialogForm},
  props: {
    value: String,
    label: String,
    disabled: Boolean,
    tabindex: String,
    required: Boolean,
    suppliersOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      search: null,
      searchTimer: null,
      data: [],
      searchStringLength: 0,
      hideNoText: false,
      currentSearchTerm: null,
      skipNext: false,
    }
  },
  methods: {
    ruleRequired,

    /**
     * Allow to skip single search terms.
     * Required as otherwise updating the autocomplete-box via vuejs will update the variables and start another search.
     */
    skipNextSearch(){
      this.skipNext = true;
    },

    resetTypeAhead() {
      this.data = [];
      this.search = null;
      this.inputVal = null;
    },

    onAddClick() {
      this.$refs.customerDialogForm.openDialogWithNameSupplierOnly(this.currentSearchTerm);
      this.resetTypeAhead();
      this.hideNoText = true;
    },

    parseCustomer(customer) {
      this.initWithoutFetch(customer);
    },

    /**
     * Will start to load the typeahead customer data after a short timer.
     * Timer is reset if user inputs something.
     *
     * @param term to search
     */
    fetchSearchDataTimer(term) {
      // cancel pending call
      clearTimeout(this.searchTimer);

      if(this.skipNext){
        this.isLoading = false;
        this.skipNext = false;
        return;
      }

      // delay new call 500ms
      this.searchTimer = setTimeout(() => {
        if(term !== null && term.includes('+')) {
          // replacing '+' sign with its code
          // because plus is not encoded by encodeURI
          term = term.replace("+", "%2B")
        }

        this.currentSearchTerm = term;

        CustomerService.typeahead(term, this.suppliersOnly,
          (res) => {
            this.searchStringLength = term.length;
            this.data = DataUtils.parseDataToObjectArray(res.data, Customer);
            this.isLoading = false;

            if(this.data.length === 1) {
              this.inputVal = this.data[0].id;
              this.inputValName = this.data[0].name;
              this.skipNextSearch(); //as we set the value automatically, it will attempt to search the textbox again -> skip
            } else if (this.data.length === 0) {
              this.hideNoText = false;
            }
          }
        )
      }, 500)
    },

    /**
     * Will initialise the data list with the given customer id and entry.
     *
     * @param customerId to initialise autocomplete list with
     */
    initialise(customerId) {
      if (customerId) {
        CustomerService.readScaledDownFromAPI(customerId,
          (res) => {
            let parsedCustomer = DataUtils.parseDataToSingleObject(res.data, Customer);
            this.data = new Array();
            this.data.push(parsedCustomer);
            this.search = parsedCustomer.name;
          },
          (error) => {
            this.$logger.error(error);
          }
        )
      }
    },
    initWithoutFetch(item) {
      if (item) {
        this.data.push(item);
        this.search = item.name;
        this.inputVal = item.id;
      }
    }
  },
  computed: {
    inputVal: {
      get() {
        return this.value;
      },
      set(val) {
        this.hideNoText = !!val;
        this.$emit('input', val);
      }
    },
    inputValName: {
      get() {
        return "";
      },
      set(val) {
        this.$emit('inputName', val);
      }
    },
    computedDataText() {
      if (this.searchStringLength <= 2) {
        return this.$t('components:common.more-char')
      } else if (this.searchStringLength > 2) {
        return this.$t('components:common.no-data-found', {name: this.$t('common:customer_plural')})
      } else {
        return '';
      }
    }
  },
  watch: {
    search(val) {
      if (val !== null) {
        if (val == undefined || val.length <= 2) {
          this.data = [];
        }
        this.isLoading = true
        this.fetchSearchDataTimer(val);
      }
    }
  }

}
</script>
